/** Screens not designed for phones (shared desktopOnlyScreen): a card with the link instead of a squashed desktop table. */
import { Copy, Mail, Monitor } from 'lucide-react';
import { APP_NAME } from '@tradeoutlet/shared';
import { useMe } from '@/lib/hooks';
import { useBrand } from '@/lib/branding';
import { useUi } from '@/lib/store';
import { BackHeader } from './buildings/common';

/** The page's address and an email to yourself with it (opens your mail app; nothing is sent by the Hub). */
export function desktopLink(href: string, name: string, email: string | null | undefined, title: string = APP_NAME) {
  const subject = `${title}: ${name}`;
  return { url: href, mailto: `mailto:${encodeURIComponent(email ?? '')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(href)}` };
}

/** t331u: `back` (the shell's full-page stand-in) adds a Back header, so the page is never a dead end in the installed app. */
export function DesktopOnlyCard({ name, href, line, back }: { name: string; href?: string; line?: string; back?: string }) {
  const toast = useUi(s => s.toast);
  const email = useMe().data?.profile.email;
  const title = useBrand().title;
  const link = desktopLink(href ?? window.location.href, name, email, title);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); toast('Link copied', 'success'); } catch { toast('Couldn’t copy the link', 'error'); }
  };
  const btn = 'flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl px-4 text-base font-medium';
  return (
    <div className="flex h-full flex-col" data-testid="desktop-only">
    {back && <BackHeader fallback={back} title={name} />}
    <div className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 p-5 text-center dark:border-slate-800">
        <Monitor className="mx-auto h-8 w-8 text-slate-500" aria-hidden />
        <h1 className="mt-2 text-lg font-semibold">{name}</h1>
        <p className="text-sm text-slate-500">{line ?? 'Best on a computer'}</p>
        <div className="mt-4 space-y-2">
          <button type="button" onClick={() => void copy()} className={`${btn} bg-accent text-on-accent active:bg-accent-pressed`} data-testid="desktop-copy"><Copy className="h-5 w-5" />Copy link</button>
          <a href={link.mailto} className={`${btn} border border-slate-300 text-slate-800 dark:border-slate-700 dark:text-slate-100`} data-testid="desktop-email"><Mail className="h-5 w-5" />Email me the link</a>
        </div>
      </div>
    </div>
    </div>
  );
}
